// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/GuestWelcomeCard.tsx
// 🔧 PURPOSE: La card della vista ospite che risale sul carosello (-72px),
//             come la card del soggiorno sull'intestazione.
//             Due forme:
//             - di solito (mockup Ospite): "Hai già prenotato?" con ACCEDI
//               CON LA PRENOTAZIONE; il riquadro crema per chi ha prenotato
//               da un altro sito o da un portale esterno → check-in online
//               (dopo la conferma entra da solo); "Non hai ancora
//               prenotato?" e PRENOTA UN SOGGIORNO;
//             - check-in inviato da questo telefono e in attesa dello
//               smistamento (mockup Attesa, `claimPending`): la pillola IN
//               ATTESA DI CONFERMA, i tre passi (inviato ✓, abbiniamo la
//               prenotazione, entri in automatico) e "Accedi comunque" per
//               chi ha già numero di prenotazione ed email.
//             Il check-in da fuori è sempre /check-in: quello di gruppo
//             chiede di essere già dentro.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useHomeSession } from './HomeSessionHost';
import { Callout, CardShell, FOCUS_RING, GoldButton, INK_TEXT, InkButton, LineButton, MUTED_TEXT, Pill } from './ui';

export interface GuestWelcomeCardProps {
  /**
   * Un check-in inviato da qui aspetta lo smistamento (il server ha già
   * risposto "pending"): al posto di "Hai già prenotato?" la card d'attesa.
   */
  claimPending?: boolean;
  className?: string;
}

const CARD_TITLE = cn('m-0 font-display text-[20px] font-bold leading-tight', INK_TEXT);

/** "— Non hai ancora prenotato? —" e PRENOTA UN SOGGIORNO, in fondo a tutte e due le forme. */
const BookBlock: React.FC = () => {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center gap-2.5">
        <span aria-hidden="true" className="h-px flex-grow bg-nva-edge dark:bg-stone-700" />
        <span className={cn('text-[12.5px] leading-snug', MUTED_TEXT)}>{t('home.guest.notBooked')}</span>
        <span aria-hidden="true" className="h-px flex-grow bg-nva-edge dark:bg-stone-700" />
      </div>
      <GoldButton full to="/book" className="min-h-[50px]">
        {t('home.guest.bookCta')}
      </GoldButton>
    </>
  );
};

type StepState = 'done' | 'current' | 'next';

/** I tre passi dell'attesa: il primo fatto, il secondo in corso. */
const ClaimSteps: React.FC = () => {
  const { t } = useTranslation();
  const steps: { key: string; label: string; state: StepState }[] = [
    { key: 'sent', label: t('checkInClaim.stepSent'), state: 'done' },
    { key: 'matching', label: t('checkInClaim.stepMatching'), state: 'current' },
    { key: 'auto', label: t('checkInClaim.stepAuto'), state: 'next' },
  ];
  return (
    <ol className="m-0 flex list-none flex-col gap-2 rounded-xl border border-nva-hairline bg-nva-bone px-3 py-2.5 dark:border-stone-700 dark:bg-stone-900">
      {steps.map((step, i) => (
        <li key={step.key} aria-current={step.state === 'current' ? 'step' : undefined} className="flex items-center gap-2.5">
          {step.state === 'done' ? (
            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-nva-espresso text-nva-gold dark:bg-stone-100 dark:text-nva-gold-text">
              <Check className="h-3 w-3" strokeWidth={3.5} aria-hidden="true" />
              <span className="sr-only">{t('home.before.done')}:</span>
            </span>
          ) : (
            <span
              aria-hidden="true"
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-nva-gold text-[11px] font-extrabold leading-none text-nva-gold-text dark:text-nva-gold-dark"
            >
              {i + 1}
            </span>
          )}
          <span
            className={cn(
              'text-[13.5px] leading-snug',
              step.state === 'next' ? MUTED_TEXT : cn('font-bold', INK_TEXT),
            )}
          >
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
};

const GuestWelcomeCard: React.FC<GuestWelcomeCardProps> = ({ claimPending = false, className }) => {
  const { t } = useTranslation();
  const { openLogin } = useHomeSession();
  const titleId = 'home-guest-welcome-title';

  return (
    <div className={cn('relative -mt-[72px] px-4', className)}>
      <CardShell raised as="section" aria-labelledby={titleId} className="flex flex-col gap-3 p-4">
        {claimPending ? (
          <>
            <Pill tone="cream" className="gap-1.5 px-2.5 py-[5px] tracking-[0.12em] text-nva-gold-text">
              <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-nva-gold" />
              {t('checkInClaim.pendingChip')}
            </Pill>
            <div className="flex flex-col gap-1">
              <h2 id={titleId} className={cn(CARD_TITLE, 'text-[21px]')}>
                {t('checkInClaim.pendingTitle')}
              </h2>
              <p className={cn('m-0 text-[14px] leading-[1.4]', MUTED_TEXT)}>{t('checkInClaim.pendingText')}</p>
            </div>
            <ClaimSteps />
            <button
              type="button"
              onClick={() => openLogin()}
              className={cn(
                'min-h-[44px] self-center rounded-lg px-2 text-[13.5px] font-bold underline underline-offset-[3px]',
                INK_TEXT,
                FOCUS_RING,
              )}
            >
              {t('checkInClaim.loginAnyway')}
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-[3px]">
              <h2 id={titleId} className={CARD_TITLE}>
                {t('home.guest.booked.title')}
              </h2>
              <p className={cn('m-0 text-[14px] leading-snug', MUTED_TEXT)}>{t('home.guest.booked.desc')}</p>
            </div>
            <InkButton full onClick={() => openLogin()} className="min-h-[50px]">
              {t('home.guest.booked.cta')}
            </InkButton>
            <Callout
              icon={<ClipboardCheck className="text-nva-gold-text dark:text-nva-gold-dark" />}
              title={t('home.guest.external.title')}
              action={
                <LineButton full to="/check-in" className="bg-nva-card dark:bg-stone-900">
                  {t('home.guest.external.cta')}
                </LineButton>
              }
            >
              {t('home.guest.external.desc')}
            </Callout>
          </>
        )}
        <BookBlock />
      </CardShell>
    </div>
  );
};

export default GuestWelcomeCard;
